import React from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Checkbox } from '@/common/ui/checkbox'
import { Badge } from '@/common/ui/badge'
import { Label } from '@/common/ui/label'
import { RadioGroup, RadioGroupItem } from '@/common/ui/radio-group'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { ChevronDown, X } from 'lucide-react'

type FilterMode = 'include' | 'exclude' | 'none'

interface FilterItem {
	id: string
	name: string
	displayName?: string
}

interface FilterSelectorProps {
	label: string
	items: FilterItem[] | undefined
	includedIds: string[]
	excludedIds: string[]
	onIncludedChange: (ids: string[]) => void
	onExcludedChange: (ids: string[]) => void
	placeholder?: string
	searchPlaceholder?: string
	disabled?: boolean
}

export const FilterSelector = ({
	label,
	items,
	includedIds,
	excludedIds,
	onIncludedChange,
	onExcludedChange,
	placeholder = 'เลือกรายการ',
	searchPlaceholder = 'ค้นหา...',
	disabled = false,
}: FilterSelectorProps) => {
	const [search, setSearch] = React.useState('')
	const [isOpen, setIsOpen] = React.useState(false)
	const [scrollElement, setScrollElement] =
		React.useState<HTMLDivElement | null>(null)

	// Determine initial mode based on which array has values
	const getInitialMode = (): FilterMode => {
		if (includedIds.length > 0) return 'include'
		if (excludedIds.length > 0) return 'exclude'
		return 'none'
	}

	// Use local state for mode to allow switching even when arrays are empty
	const [mode, setMode] = React.useState<FilterMode>(getInitialMode)

	// Sync mode with external changes to includedIds/excludedIds
	React.useEffect(() => {
		if (includedIds.length > 0 && mode !== 'include') {
			setMode('include')
		} else if (excludedIds.length > 0 && mode !== 'exclude') {
			setMode('exclude')
		} else if (
			includedIds.length === 0 &&
			excludedIds.length === 0 &&
			mode !== 'none'
		) {
			// Only reset to 'none' if user hasn't explicitly selected a mode
			// This is handled by handleModeChange, so we don't auto-reset here
		}
	}, [includedIds, excludedIds, mode])

	const selectedIds =
		mode === 'include' ? includedIds : mode === 'exclude' ? excludedIds : []

	const filteredItems = React.useMemo(() => {
		if (!search) return items
		return items?.filter(
			(item) =>
				item.name.toLowerCase().includes(search.toLowerCase()) ||
				item.displayName?.toLowerCase().includes(search.toLowerCase()),
		)
	}, [items, search])

	// Virtual scrolling setup - only when we have the scroll element
	const rowVirtualizer = useVirtualizer({
		count: filteredItems?.length ?? 0,
		getScrollElement: () => scrollElement,
		estimateSize: () => 36, // Height of each item (py-1.5 + content)
		overscan: 5, // Render 5 extra items outside viewport for smooth scrolling
	})

	// Reset scroll position when search changes
	React.useEffect(() => {
		if (scrollElement) {
			scrollElement.scrollTop = 0
		}
	}, [search])

	const handleModeChange = (newMode: FilterMode) => {
		if (newMode === 'none') {
			// Clear both arrays
			onIncludedChange([])
			onExcludedChange([])
			setMode('none')
		} else if (newMode === 'include') {
			// Transfer any excluded to included
			if (excludedIds.length > 0) {
				onIncludedChange(excludedIds)
				onExcludedChange([])
			}
			setMode('include')
		} else if (newMode === 'exclude') {
			// Transfer any included to excluded
			if (includedIds.length > 0) {
				onExcludedChange(includedIds)
				onIncludedChange([])
			}
			setMode('exclude')
		}
	}

	const handleItemToggle = (itemId: string) => {
		const isSelected = selectedIds.includes(itemId)
		const newValues = isSelected
			? selectedIds.filter((id) => id !== itemId)
			: [...selectedIds, itemId]

		if (mode === 'include') {
			onIncludedChange(newValues)
			onExcludedChange([])
		} else if (mode === 'exclude') {
			onExcludedChange(newValues)
			onIncludedChange([])
		}
	}

	const handleClear = () => {
		onIncludedChange([])
		onExcludedChange([])
		setMode('none')
	}

	const handleRemoveItem = (itemId: string) => {
		if (mode === 'include') {
			const newIds = includedIds.filter((id) => id !== itemId)
			onIncludedChange(newIds)
			// If no more items, reset mode to none
			if (newIds.length === 0) {
				setMode('none')
			}
		} else if (mode === 'exclude') {
			const newIds = excludedIds.filter((id) => id !== itemId)
			onExcludedChange(newIds)
			// If no more items, reset mode to none
			if (newIds.length === 0) {
				setMode('none')
			}
		}
	}

	const getItemName = (id: string) => {
		const item = items?.find((i) => i.id === id)
		return item?.displayName || item?.name || id
	}

	const selectedCount = selectedIds.length

	// Generate unique IDs for radio buttons using a sanitized label
	const sanitizedLabel = label
		.replace(/\s+/g, '-')
		.replace(/[^a-zA-Z0-9-]/g, '')
	const uniqueId = React.useId()

	return (
		<div className="space-y-3 rounded-md border p-3">
			<div className="flex items-center justify-between">
				<Label className="text-sm font-medium">{label}</Label>
				{selectedCount > 0 && (
					<Badge
						variant={mode === 'exclude' ? 'destructive' : 'secondary'}
						className="text-xs"
					>
						{mode === 'include' ? 'รวม' : 'ไม่รวม'} {selectedCount} รายการ
					</Badge>
				)}
			</div>

			{/* Mode Toggle */}
			<RadioGroup
				value={mode}
				onValueChange={(value) => handleModeChange(value as FilterMode)}
				className="flex gap-4"
				disabled={disabled}
			>
				<div className="flex items-center space-x-2">
					<RadioGroupItem
						value="none"
						id={`${uniqueId}-${sanitizedLabel}-none`}
					/>
					<Label
						htmlFor={`${uniqueId}-${sanitizedLabel}-none`}
						className="cursor-pointer text-xs"
					>
						ไม่กรอง
					</Label>
				</div>
				<div className="flex items-center space-x-2">
					<RadioGroupItem
						value="include"
						id={`${uniqueId}-${sanitizedLabel}-include`}
					/>
					<Label
						htmlFor={`${uniqueId}-${sanitizedLabel}-include`}
						className="cursor-pointer text-xs"
					>
						รวมเฉพาะ
					</Label>
				</div>
				<div className="flex items-center space-x-2">
					<RadioGroupItem
						value="exclude"
						id={`${uniqueId}-${sanitizedLabel}-exclude`}
					/>
					<Label
						htmlFor={`${uniqueId}-${sanitizedLabel}-exclude`}
						className="cursor-pointer text-xs"
					>
						รวมทั้งหมดยกเว้น
					</Label>
				</div>
			</RadioGroup>

			{/* Multi-select dropdown - only show when mode is not 'none' */}
			{mode !== 'none' && (
				<>
					<Popover
						open={isOpen}
						onOpenChange={(open) => {
							setIsOpen(open)
							if (!open) {
								setSearch('')
							}
						}}
					>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								role="combobox"
								className="w-full justify-between"
								disabled={disabled}
							>
								<span className="truncate">
									{selectedCount > 0
										? `${selectedCount} รายการที่เลือก`
										: placeholder}
								</span>
								<ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-full p-0" align="start">
							<div className="space-y-2 p-2">
								<Input
									placeholder={searchPlaceholder}
									className="h-8 text-sm"
									value={search}
									onChange={(e) => setSearch(e.target.value)}
								/>
								{/* Virtual scrolling container */}
								{filteredItems && filteredItems.length > 0 ? (
									<div
										ref={setScrollElement}
										className="h-[200px] overflow-y-auto"
									>
										<div
											style={{
												height: `${rowVirtualizer.getTotalSize()}px`,
												width: '100%',
												position: 'relative',
											}}
										>
											{rowVirtualizer.getVirtualItems().map((virtualRow) => {
												const item = filteredItems?.[virtualRow.index]
												if (!item) return null

												return (
													<div
														key={item.id}
														style={{
															position: 'absolute',
															top: 0,
															left: 0,
															width: '100%',
															height: `${virtualRow.size}px`,
															transform: `translateY(${virtualRow.start}px)`,
														}}
													>
														<div
															className="flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent"
															onClick={() => handleItemToggle(item.id)}
														>
															<Checkbox
																checked={selectedIds.includes(item.id)}
																className="pointer-events-none h-4 w-4"
															/>
															<span className="text-xs">
																{item.displayName || item.name}
															</span>
														</div>
													</div>
												)
											})}
										</div>
									</div>
								) : (
									<div className="flex h-[200px] items-center justify-center">
										<div className="text-center text-xs text-muted-foreground">
											ไม่พบรายการ
										</div>
									</div>
								)}
								{selectedCount > 0 && (
									<Button
										variant="ghost"
										className="w-full justify-center text-xs"
										onClick={handleClear}
									>
										ล้างทั้งหมด
									</Button>
								)}
							</div>
						</PopoverContent>
					</Popover>

					{/* Selected badges */}
					{selectedCount > 0 && (
						<div className="flex flex-wrap gap-1">
							{selectedIds.map((id) => (
								<Badge
									key={id}
									variant={mode === 'exclude' ? 'destructive' : 'secondary'}
									className="gap-1 rounded-sm px-2 py-0.5 text-xs font-normal"
								>
									{getItemName(id)}
									<X
										className="h-3 w-3 cursor-pointer hover:opacity-70"
										onClick={() => handleRemoveItem(id)}
									/>
								</Badge>
							))}
						</div>
					)}
				</>
			)}
		</div>
	)
}
